<template>
    <div>
        <kt-datatable
            :model="submissionModel"
            :data="rows"
            :filter="datatableFilter"
            :meta="{ tableClass: 'table', rowClass: getRowClass }"
            :update-function="handleDatatableUpdate"
            :hide-search="false"
            @cell-click="handleCellClick"
            storage-key="form-submission-list"
        >
            <template #header_select="scope">
                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :checked="selectAll"
                        @change="toggleSelectAll"
                        @click.stop
                        title="Select all on this page"
                    />
                </div>
            </template>
            
            <template #view_select="scope">
                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :checked="selectedRows.includes(scope.row.Id)"
                        @change="toggleRowSelection(scope.row.Id)"
                        @click.stop
                    />
                </div>
            </template>

            <template #filters="scope">
                <div class="col">
                    <div class="row">
                        <div class="col-auto">
                            <label class="form-label">Status</label>
                            <select 
                                class="form-select form-select-sm"
                                v-model="currentFilter.status"
                                @change="applyFilter"
                            >
                                <option value="">All</option>
                                <option v-for="status in statusOptions" :key="status.value" :value="status.value">
                                    {{ status.label }}
                                </option>
                            </select>
                        </div>
                        <div class="col-auto" v-if="!props.requestId">
                            <label class="form-label">Template</label>
                            <select 
                                class="form-select form-select-sm"
                                v-model="currentFilter.templateId"
                                @change="applyFilter"
                            >
                                <option value="">All Templates</option>
                                <option v-for="template in templates" :key="template.Id" :value="template.Id">
                                    {{ template.Title }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>
            </template>

            <template #after-search="scope">
                <div class="btn-group">
                    <button @click="refresh()" 
                        class="btn btn-sm btn-outline-secondary me-2"
                    >Refresh <i class="fa fa-sync"></i></button>

                    <button 
                        v-if="!props.disableActions"
                        @click="newRecord()" 
                        class="btn btn-sm btn-outline-primary"
                    >New <i class="fa fa-plus"></i></button>
                </div>
            </template>

            <template #under-header>
                <div v-if="selectedRows.length > 0" class="mb-0 p-2 actions-row">
                    <div class="d-flex align-items-center gap-2">
                        <span class="fw-bold text-primary">{{ selectedRows.length }} selected</span>
                        <button 
                            class="btn btn-sm btn-outline-danger"
                            @click="showDeleteModal = true"
                            title="Delete Selected"
                        >
                            <i class="fas fa-trash"></i> Delete
                        </button>
                    </div>
                </div>
            </template>

            <template #view_string="scope">
                <span v-if="scope.column.name === 'TemplateName'">
                    {{ scope.row.Template?.Title || 'N/A' }}
                </span>
                <span v-else-if="scope.column.name === 'Status'">
                    <span :class="getStatusBadgeClass(scope.row.Status)">
                        {{ getStatusLabel(scope.row.Status) }}
                    </span>
                </span>
                <span v-else-if="scope.column.name === 'DateCreated'">
                    {{ formatDate(scope.row.DateCreated) }}
                </span>
                <span v-else-if="scope.column.name === 'DateSubmitted'">
                    {{ formatDate(scope.row.DateSubmitted) }}
                </span>
                <span v-else>
                    {{ scope.value }}
                </span>
            </template>
        </kt-datatable>

        <!-- Delete Confirmation Modal -->
        <div v-if="showDeleteModal" class="modal fade show" style="display: block;" tabindex="-1">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Confirm Delete</h5>
                        <button type="button" class="btn-close" @click="showDeleteModal = false"></button>
                    </div>
                    <div class="modal-body">
                        <p>Are you sure you want to delete {{ selectedRows.length }} form submission(s)?</p>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="showDeleteModal = false">Cancel</button>
                        <button type="button" class="btn btn-danger" @click="deleteSelected">Delete</button>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="showDeleteModal" class="modal-backdrop fade show"></div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import Api, { ApiResponse, FormSubmissionModel, FormSubmissionFilter, FormTemplateModel } from "@/services/ApiService";
import Emitter from "@/services/Emitter";
import KtDatatable from "@/components/UI/KtDatatable.vue";
import Helper, { DateFormat } from "@/services/Helper";

const props = defineProps({
    requestId: { type: [Number, String], default: null },
    companyId: { type: [Number, String], default: null },
    templateId: { type: String, default: null },
    embedded: { type: Boolean, default: false },
    hideFilters: { type: Boolean, default: false },
    disableActions: { type: Boolean, default: false }
});

const emit = defineEmits(['edit', 'new']);

// Data
const rows = ref<FormSubmissionModel[]>([]);
const total = ref(0);
const selectedRows = ref<string[]>([]);
const showDeleteModal = ref(false);
const templates = ref<FormTemplateModel[]>([]);

// Status options
const statusOptions = [
    { value: 0, label: 'Draft' },
    { value: 1, label: 'Submitted' },
    { value: 2, label: 'Approved' },
    { value: 3, label: 'Rejected' }
];

// Filter state - use any to allow dynamic filter properties
const currentFilter = ref<any>({
    Page: 0,
    Size: 25,
    Status: undefined,
    TemplateId: undefined,
    RequestId: props.requestId ? [Number(props.requestId)] : undefined,
    CompanyId: props.companyId ? [Number(props.companyId)] : undefined,
    SearchTerm: undefined,
    IncludeTemplate: true
});

const datatableFilter = computed(() => ({
    ...currentFilter.value,
    total: total.value
}));

const selectAll = computed(() => {
    return rows.value.length > 0 && rows.value.every(row => selectedRows.value.includes(row.Id!));
});

// Datatable model
const submissionModel = [
    {
        name: "Select",
        label: "",
        type: "select",
        display: true,
        width: 40
    },
    {
        name: "TemplateName",
        label: "Form",
        type: "string",
        display: true,
        sortable: true
    },
    {
        name: "SubmitterName",
        label: "Submitter",
        type: "string",
        display: true,
        sortable: true
    },
    {
        name: "SubmitterEmail",
        label: "Email",
        type: "string",
        display: true
    },
    {
        name: "Status",
        label: "Status",
        type: "string",
        display: true
    },
    {
        name: "DateCreated",
        label: "Created",
        type: "string",
        display: true,
        sortable: true
    },
    {
        name: "DateSubmitted",
        label: "Submitted",
        type: "string",
        display: true,
        sortable: true
    }
];

// Methods
const loadData = async () => {
    try {
        const response: ApiResponse<FormSubmissionModel[]> = await Api.loadFormSubmissions(currentFilter.value);
        rows.value = response.Data || [];
        total.value = response.Meta?.Total || 0;
    } catch (error) {
        console.error('Failed to load form submissions:', error);
        rows.value = [];
        total.value = 0;
    }
};

const loadTemplates = async () => {
    try {
        const response = await Api.loadFormTemplates({ IsActive: true });
        templates.value = response.Data || [];
    } catch (error) {
        console.error('Failed to load templates:', error);
    }
};

const handleDatatableUpdate = async (filter: any) => {
    currentFilter.value = { ...currentFilter.value, ...filter };
    await loadData();
};

const applyFilter = () => {
    currentFilter.value.Page = 0;
    if (currentFilter.value.status !== undefined && currentFilter.value.status !== '') {
        currentFilter.value.Status = [Number(currentFilter.value.status)];
    } else {
        currentFilter.value.Status = undefined;
    }
    if (currentFilter.value.templateId) {
        currentFilter.value.TemplateId = [currentFilter.value.templateId];
    } else {
        currentFilter.value.TemplateId = undefined;
    }
    loadData();
};

const refresh = () => {
    loadData();
};

const newRecord = () => {
    if (props.embedded) {
        emit('new');
    } else {
        Emitter.emit('open:formSubmissionForm', { requestId: props.requestId, companyId: props.companyId });
    }
};

const editRecord = (id: string) => {
    if (props.embedded) {
        emit('edit', id);
    } else {
        Emitter.emit('open:formSubmissionForm', id);
    }
};

const handleCellClick = (row: any, column: any, value: any, rowIndex: number) => {
    if (column.name !== 'Select') {
        editRecord(row.Id);
    }
};

const getRowClass = (row: FormSubmissionModel) => {
    if (row.Status === 3) return 'table-danger';
    if (row.Status === 2) return 'table-success';
    if (row.Status === 1) return 'table-info';
    return '';
};

const getStatusBadgeClass = (status: number) => {
    switch (status) {
        case 0: return 'badge bg-secondary';
        case 1: return 'badge bg-primary';
        case 2: return 'badge bg-success';
        case 3: return 'badge bg-danger';
        default: return 'badge bg-secondary';
    }
};

const getStatusLabel = (status: number) => {
    const option = statusOptions.find(o => o.value === status);
    return option?.label || 'Unknown';
};

const toggleSelectAll = () => {
    if (selectAll.value) {
        selectedRows.value = [];
    } else {
        selectedRows.value = rows.value.map(row => row.Id!);
    }
};

const toggleRowSelection = (id: string) => {
    const index = selectedRows.value.indexOf(id);
    if (index === -1) {
        selectedRows.value.push(id);
    } else {
        selectedRows.value.splice(index, 1);
    }
};

const deleteSelected = async () => {
    try {
        for (const id of selectedRows.value) {
            await Api.deleteFormSubmission(id);
        }
        selectedRows.value = [];
        showDeleteModal.value = false;
        await loadData();
    } catch (error) {
        console.error('Failed to delete form submissions:', error);
    }
};

const formatDate = (date: string | undefined) => {
    if (!date) return '';
    try {
        const d = new Date(date);
        return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
        return date;
    }
};

// Watch for prop changes
watch(() => props.requestId, () => {
    if (props.requestId) {
        currentFilter.value.RequestId = [Number(props.requestId)];
    } else {
        currentFilter.value.RequestId = undefined;
    }
    loadData();
});

// Lifecycle
onMounted(() => {
    loadData();
    loadTemplates();
    Emitter.on('datatable-refresh', (key: string) => {
        if (key === 'form-submission-list') {
            loadData();
        }
    });
});

onUnmounted(() => {
    // Cleanup handled by Vue
});
</script>

<style scoped>
.actions-row {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}
</style>
